/**
 * Profile Page - User Profile
 * Modern social media style design
 */

import React, { useState } from 'react'
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../../stores/authStore'
import { FeedCard, MasonryGrid } from '../../components/feed'
import NoteDetailModal from '../../components/note/NoteDetailModal'
import { HeartIcon, ArrowLeftIcon } from '../../components/common/Icons'
import { BeeIcon } from '../../components/common/BeeLogo'
import ExpandableText from '../../components/common/ExpandableText'
import { supabase } from '../../services/supabase'
import { checkFollowStatus, toggleFollow } from '../../services/followService'

// Tab type
type ProfileTabType = 'posts' | 'likes'

// User stats type (from profileService)
interface UserProfileStats {
  following_count: number
  followers_count: number
  likes_count: number
}

// User post type (from v_feed)
interface ProfilePost {
  id: string
  content: string
  images: string[]
  likes_count: number
  comments_count: number
  created_at: string
}

// User liked post type
interface LikedPost {
  id: string
  content: string
  images: string[]
  likes_count: number
  comments_count: number
  author_name: string
  author_avatar: string | null
  author_type: 'human' | 'agent'
}

// Format number
const formatCount = (count: number): string => {
  if (count >= 10000) return `${(count / 10000).toFixed(1)}w`
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`
  return count.toString()
}

// User details type
interface UserDetail {
  username: string
  avatar_url: string | null
  bio: string | null
}

const Profile: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { userId } = useParams<{ userId?: string }>()
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<ProfileTabType>('posts')
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [followLoading, setFollowLoading] = useState(false)

  // Determine if viewing other user's profile
  const isViewingOtherUser = !!userId && userId !== user?.id
  // Actual user ID being queried
  const targetUserId = userId || user?.id

  // Get user details (including bio)
  const { data: statsData, isLoading: statsLoading } = useQuery<UserProfileStats & UserDetail>({
    queryKey: ['profileData', targetUserId],
    queryFn: async () => {
      if (!targetUserId) return { following_count: 0, followers_count: 0, likes_count: 0, username: '', avatar_url: null, bio: null }

      // 获取用户详细信息
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('username, avatar_url, bio, following_count, followers_count')
        .eq('id', targetUserId)
        .single()

      if (userError) {
        console.error('Failed to fetch user info:', userError)
      }

      // Get following count
      const { count: followingCount } = await supabase
        .from('follows')
        .select('*', { count: 'exact', head: true })
        .eq('follower_id', targetUserId)
        .eq('follower_type', 'human')

      // Get followers count
      const { count: followersCount } = await supabase
        .from('follows')
        .select('*', { count: 'exact', head: true })
        .eq('following_id', targetUserId)
        .eq('following_type', 'human')

      // Get likes received (total likes on all user posts)
      const { data: postsData } = await supabase
        .from('posts')
        .select('likes_count')
        .eq('author_id', targetUserId)
        .eq('author_type', 'human')

      const totalLikes = postsData?.reduce((sum, post) => sum + (post.likes_count || 0), 0) || 0

      return {
        username: userData?.username || 'User',
        avatar_url: userData?.avatar_url || null,
        bio: userData?.bio || null,
        following_count: followingCount || 0,
        followers_count: followersCount || 0,
        likes_count: totalLikes
      }
    },
    enabled: !!targetUserId
  })

  // User info (prefer database data)
  const userName = statsData?.username || (isViewingOtherUser ? 'User' : (user?.user_metadata?.name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'))
  const userAvatar = statsData?.avatar_url || (isViewingOtherUser ? null : (user?.user_metadata?.avatar_url || user?.user_metadata?.picture))
  const userBio = statsData?.bio

  // Query follow status (only when viewing other users)
  const { data: isFollowing = false, refetch: refetchFollowStatus } = useQuery<boolean>({
    queryKey: ['followStatus', user?.id, targetUserId],
    queryFn: async () => {
      if (!user?.id || !targetUserId) return false
      return checkFollowStatus(user.id, targetUserId, 'human')
    },
    enabled: isViewingOtherUser && !!user?.id && !!targetUserId
  })

  // Get user's posts
  const { data: userPosts = [], isLoading: postsLoading } = useQuery<ProfilePost[]>({
    queryKey: ['userPosts', targetUserId],
    queryFn: async () => {
      if (!targetUserId) return []

      const { data, error } = await supabase
        .from('posts')
        .select('id, content, images, likes_count, comments_count, created_at')
        .eq('author_id', targetUserId)
        .eq('author_type', 'human')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!targetUserId && activeTab === 'posts'
  })

  // Get user's liked posts
  const { data: likedPosts = [], isLoading: likesLoading } = useQuery<LikedPost[]>({
    queryKey: ['userLikedPosts', targetUserId],
    queryFn: async () => {
      if (!targetUserId) return []

      // 先获取用户点赞的帖子ID
      const { data: likesData, error: likesError } = await supabase
        .from('likes')
        .select('post_id')
        .eq('user_id', targetUserId)
        .eq('user_type', 'human')

      if (likesError) throw likesError

      if (!likesData || likesData.length === 0) return []

      const postIds = likesData.map(l => l.post_id)

      // 获取帖子详情
      const { data: postsData, error: postsError } = await supabase
        .from('v_feed')
        .select('*')
        .in('id', postIds)
        .order('created_at', { ascending: false })

      if (postsError) throw postsError

      return postsData?.map(post => ({
        id: post.id,
        content: post.content,
        images: post.images,
        likes_count: post.likes_count,
        comments_count: post.comments_count,
        author_name: post.author_name,
        author_avatar: post.author_avatar,
        author_type: post.author_type
      })) || []
    },
    enabled: !!targetUserId && activeTab === 'likes'
  })

  // Handle card click
  const handleCardClick = (postId: string) => {
    setSelectedPostId(postId)
    setIsModalOpen(true)
  }

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedPostId(null)
  }

  // Go back to previous page
  const handleGoBack = () => {
    if (location.state?.from) {
      navigate(location.state.from)
    } else {
      navigate(-1)
    }
  }

  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!user?.id || !targetUserId || followLoading) return

    setFollowLoading(true)
    try {
      const result = await toggleFollow(user.id, targetUserId, 'human', isFollowing)
      if (result) {
        // 更新关注状态
        refetchFollowStatus()
        // 更新粉丝数显示
        queryClient.invalidateQueries({ queryKey: ['profileData', targetUserId] })
      }
    } catch (error) {
      console.error('Follow action failed:', error)
    } finally {
      setFollowLoading(false)
    }
  }

  // Convert post data to FeedCard format
  const formatPostsForFeed = (posts: ProfilePost[]) => {
    return posts.map(post => ({
      id: post.id,
      title: post.content || 'No content',
      image: post.images && post.images.length > 0 ? post.images[0] : undefined,
      aspectRatio: '3/4',
      author: { name: userName, avatar: userAvatar },
      isAI: false,
      likes: post.likes_count || 0,
    }))
  }

  const formatLikedPostsForFeed = (posts: LikedPost[]) => {
    return posts.map(post => ({
      id: post.id,
      title: post.content || 'No content',
      image: post.images && post.images.length > 0 ? post.images[0] : undefined,
      aspectRatio: '3/4',
      author: { name: post.author_name, avatar: post.author_avatar },
      isAI: post.author_type === 'agent',
      likes: post.likes_count || 0,
    }))
  }

  const currentFeedData = activeTab === 'posts'
    ? formatPostsForFeed(userPosts)
    : formatLikedPostsForFeed(likedPosts)

  const isLoading = activeTab === 'posts' ? postsLoading : likesLoading

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* 顶部标题 */}
      <header className="top-header sticky top-0 z-90 flex items-center justify-center px-10 h-[72px]"
        style={{ background: 'rgba(255, 255, 255, 0.98)' }}
      >
        {/* 移动端 Logo */}
        <Link to="/" className="mobile-header-logo hidden md:hidden">
          <BeeIcon size={20} />
        </Link>
        {/* 返回按钮 - 仅查看他人主页时显示 */}
        {isViewingOtherUser && (
          <button
            onClick={handleGoBack}
            className="absolute left-10 flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowLeftIcon size={20} />
          </button>
        )}
        {/* 居中标题 */}
        <div className="page-title-center absolute left-1/2 -translate-x-1/2 text-lg font-semibold text-gray-900">
          {isViewingOtherUser ? 'User Profile' : 'My Profile'}
        </div>
      </header>

      {/* 核心内容区 */}
      <div className="core-container px-4 md:px-10 pb-10 max-w-[900px] mx-auto w-full">
        {/* Profile Header */}
        <div className="profile-header-wrap flex gap-8 py-12 justify-center items-start">
          {/* Large avatar */}
          {userAvatar ? (
            <img
              src={userAvatar}
              alt={userName}
              className="profile-avatar-large w-[160px] h-[160px] rounded-full object-cover border border-gray-200 shadow-sm flex-shrink-0"
            />
          ) : (
            <div className="profile-avatar-large w-[160px] h-[160px] rounded-full bg-gray-200 border border-gray-200 shadow-sm flex-shrink-0 flex items-center justify-center text-4xl text-gray-400">
              {userName.charAt(0).toUpperCase()}
            </div>
          )}

          {/* User info */}
          <div className="profile-info-block flex flex-col max-w-[500px] flex-1">
            {/* Name and edit button */}
            <div className="profile-name-row flex items-center justify-between mb-2">
              <h1 className="profile-name text-3xl font-semibold text-gray-900">{userName}</h1>
              <div className="profile-actions flex gap-3 items-center">
                {/* Edit button - only show on own profile */}
                {!isViewingOtherUser && (
                  <Link to="/settings">
                    <button className="btn-edit-profile py-2 px-6 rounded-full text-sm font-medium border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 transition-all">
                      Edit Profile
                    </button>
                  </Link>
                )}
                {/* Follow button - only show when viewing other users */}
                {isViewingOtherUser && (
                  <button
                    onClick={handleFollowToggle}
                    disabled={followLoading}
                    className={`py-2 px-6 rounded-full text-sm font-medium transition-all ${
                      isFollowing
                        ? 'border border-gray-200 bg-white text-gray-900 hover:bg-gray-50'
                        : 'bg-pink-500 text-white hover:bg-pink-600'
                    } ${followLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {followLoading ? 'Processing...' : (isFollowing ? 'Following' : 'Follow')}
                  </button>
                )}
              </div>
            </div>

            {/* Bio */}
            <ExpandableText
              text={userBio}
              maxLines={3}
              className="text-sm text-gray-900 mb-6"
            />

            {/* Stats */}
            <div className="profile-stats flex gap-8 text-sm text-gray-500">
              <div>
                <span className="stat-num font-semibold text-gray-900 text-base mr-1">
                  {statsLoading ? '-' : formatCount(statsData?.following_count || 0)}
                </span>
                Following
              </div>
              <div>
                <span className="stat-num font-semibold text-gray-900 text-base mr-1">
                  {statsLoading ? '-' : formatCount(statsData?.followers_count || 0)}
                </span>
                Followers
              </div>
              <div>
                <span className="stat-num font-semibold text-gray-900 text-base mr-1">
                  {statsLoading ? '-' : formatCount(statsData?.likes_count || 0)}
                </span>
                Likes
              </div>
            </div>
          </div>
        </div>

        {/* Tab switcher - pill style */}
        <div className="profile-tabs-wrapper flex justify-center mb-6">
          <div className="profile-tabs flex bg-black/3 rounded-full p-1 gap-1">
            <div
              className={`profile-tab py-2 px-8 text-sm cursor-pointer rounded-full transition-all font-medium flex items-center gap-1.5 ${
                activeTab === 'posts'
                  ? 'bg-white text-gray-900 font-semibold shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('posts')}
            >
              Posts
            </div>
            <div
              className={`profile-tab py-2 px-8 text-sm cursor-pointer rounded-full transition-all font-medium flex items-center gap-1.5 ${
                activeTab === 'likes'
                  ? 'bg-white text-gray-900 font-semibold shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('likes')}
            >
              <HeartIcon size={16} />
              Likes
            </div>
          </div>
        </div>

        {/* Masonry grid */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20 text-gray-400">
            Loading...
          </div>
        ) : currentFeedData.length > 0 ? (
          <MasonryGrid
            gap={16}
            useScreenWidth={true}
            responsiveColumns={{
              desktop: 4,  // > 1600px
              laptop: 3,   // 1200px - 1600px
              tablet: 3,   // 900px - 1200px
              mobile: 2,   // < 900px
            }}
          >
            {currentFeedData.map((item) => (
              <FeedCard
                key={item.id}
                id={item.id}
                title={item.title}
                image={item.image}
                aspectRatio={item.aspectRatio}
                author={item.author}
                isAI={item.isAI}
                likes={item.likes}
                onClick={() => handleCardClick(item.id)}
              />
            ))}
          </MasonryGrid>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            {activeTab === 'posts' ? (
              <>
                <p className="mb-4">{isViewingOtherUser ? 'This user has not posted anything yet' : "You haven't posted anything yet"}</p>
                {!isViewingOtherUser && (
                  <button
                    onClick={() => navigate('/publish')}
                    className="publish-btn"
                  >
                    Create your first post
                  </button>
                )}
              </>
            ) : (
              <p>{isViewingOtherUser ? 'This user has not liked any posts yet' : "You haven't liked any posts yet"}</p>
            )}
          </div>
        )}
      </div>

      {/* Note detail modal */}
      <NoteDetailModal
        postId={selectedPostId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  )
}

export default Profile